/**
 * Boardroom grid resolution helpers.
 *
 * The pinned "Boardroom" lobby card is the protocol's flagship grid —
 * grid 0 of the Harberger-Vote `UnifiedGridManager` (`HV_TAKEOVER_*`
 * env vars). Across the lobby we identify it by the stable id
 * `'boardroom'` so the canonical mock branding (name, skybox, plinth,
 * hue, description) survives a changing on-chain address. The data
 * hooks below translate that id into the live composite grid id
 * (`<ugm>-<gridId>`) at the call site so SSE / seats / adapters /
 * rewards all flow live without leaking the env address into the rest
 * of the lobby.
 *
 * If the HV env vars are unset (e.g. early SSR, unit tests) the
 * helpers return `null` and consumers fall back to the bundled
 * snapshot — the lobby never crashes when the chain isn't configured.
 */

import {
  HV_TAKEOVER_GRID_ID,
  HV_TAKEOVER_UGM_ADDRESS,
} from '@/lib/constants/addresses';

/** Stable lobby id used everywhere the pinned Boardroom card is rendered. */
export const BOARDROOM_BOARD_ID = 'boardroom';

/** True when an id matches a live v2 grid id (`0x<addr>{-/}<tokenId>`). */
export function isLiveGridId(gridId: string | undefined): gridId is string {
  return !!gridId && /^0x[a-fA-F0-9]{40}[-/]\d+$/.test(gridId);
}

/**
 * Composite live id for the Boardroom grid (e.g. `0x5455…a735f-0`).
 * Returns `null` when the HV env vars haven't been wired — callers
 * should treat this as "no live data for the boardroom yet" and keep
 * showing the snapshot fallback.
 */
export function getBoardroomLiveGridId(): string | null {
  if (!HV_TAKEOVER_UGM_ADDRESS) return null;
  return HV_TAKEOVER_GRID_ID;
}

/**
 * Translate a lobby board id into the live composite grid id when
 * available. Returns `null` for mock-only boards (dev grids etc.) so
 * downstream consumers can keep their existing "live vs snapshot"
 * branches untouched.
 */
export function resolveLobbyLiveGridId(boardId: string | undefined): string | null {
  if (!boardId) return null;
  if (boardId === BOARDROOM_BOARD_ID) return getBoardroomLiveGridId();
  if (isLiveGridId(boardId)) return boardId;
  return null;
}
